import { useContext, useState } from 'react';
import { Modal } from '@appkit4/react-components/modal';
import { PreferencesContext } from '@contexts/PreferencesProvider';
import { Button } from '@appkit4/react-components';
import styles from './Preferences.module.scss';
import classnames from 'classnames';
import { RadioGroup, Radio } from '@appkit4/react-components/radio';
import { FileUploadIcon } from '../FileUploadIcon';
import { capitalize } from 'lodash';
import styleModern from '@assets/images/preference-style-modern.svg';
import styleMinimal from '@assets/images/preference-style-minimal.svg';
import styleClassis from '@assets/images/preference-style-classic.svg';
import logoLabel from '@assets/images/preference-logo-pwc-label.svg';
import logoPwc from '@assets/images/preference-logo-pwc.svg';

export interface PreferencesFooterProps {
    onCancel: () => void;
    onSave: () => void;
    onSaveAndRegenerate: () => void;
}

export interface PreferencesContentProps {
    selectedBrand: string | null;
    setBrand: (brand: string) => void;
    selectedStyle: string | null;
    setStyle: (style: string) => void;
    uploadedStyle: File | null;
    setUploadedStyle: (file: File) => void;
    selectedLogo: string | null;
    setLogo: (logo: string) => void;
    uploadedLogo: File | null;
    setUploadedLogo: (file: File) => void;
    selectedTheme: string | null;
    setSelectedTheme: (theme: string) => void;
    selectedCorner: string | null;
    setCorner: (corner: string) => void;
}

const PreferencesFooter = (props: PreferencesFooterProps) => {
    return (
        <div className={styles['button-container']}>
            <Button kind="secondary" icon="icon-time-outline" onClick={props.onCancel}>
                Cancel
            </Button>
            <Button kind="secondary" icon="icon-save-outline" onClick={props.onSave}>
                Save
            </Button>
            <Button kind="negative" icon="icon-paper-airplane-outline" onClick={props.onSaveAndRegenerate}>
                Save & Regenerate
            </Button>
        </div>
    );
};

const PreferencesContent = (props: PreferencesContentProps) => {
    const {
        selectedBrand,
        setBrand,
        selectedStyle,
        setStyle,
        uploadedStyle,
        setUploadedStyle,
        selectedLogo,
        setLogo,
        uploadedLogo,
        setUploadedLogo,
        selectedTheme,
        setSelectedTheme,
        selectedCorner,
        setCorner,
    } = props;

    const brandOptions = [
        {
            name: 'pwc',
            title: 'PWC Brand',
            desc: 'Follows Appkit and Brand guidelines.',
        },
        {
            name: '3rd',
            title: '3rd party Design systems',
            desc: 'Customize according client needs.',
        },
    ];
    const styleOptions = [
        {
            name: 'modern',
            title: 'Modern',
            img: styleModern,
        },
        {
            name: 'classic',
            title: 'Classis',
            img: styleClassis,
        },
        {
            name: 'minimal',
            title: 'Minimal',
            img: styleMinimal,
        },
    ];
    const logoOptions = [
        {
            name: 'pwcLabel',
            img: logoLabel,
        },
        {
            name: 'pwc',
            img: logoPwc,
        },
    ];
    const themingOptions = [
        {
            name: 'blue',
        },
        {
            name: 'orange',
        },
        {
            name: 'teal',
        },
        {
            name: 'pink',
        },
        {
            name: 'red',
        },
        {
            name: 'black',
        },
    ];
    const buttonStylingOptions = [
        {
            name: 'square',
        },
        {
            name: 'rounded',
        },
        {
            name: 'pill',
        },
    ];

    const handleClickBrand = (brand: string) => {
        setBrand(brand);
    };

    const handleClickStyle = (style: string) => {
        setStyle(style);
    };

    const handleUploadStyle = (file: File) => {
        setUploadedStyle(file);
    };

    const handleUploadLogo = (file: File) => {
        setUploadedLogo(file);
    };

    const handleClickLogo = (logo: any) => {
        setLogo(logo);
    };

    const handleSelectTheme = (theme: string) => {
        setSelectedTheme(theme);
    };

    const handleSelectCorner = (corner: string) => {
        setCorner(corner);
    };

    return (
        <div className={styles['pref-content']}>
            <RadioGroup value={selectedBrand}>
                <div className={styles['pref-content-brands']}>
                    {brandOptions.map((brand, index) => {
                        return (
                            <div
                                className={classnames({
                                    [styles['pref-content-brand']]: true,
                                    [styles['selected']]: brand.name === selectedBrand,
                                })}
                                key={`brand-${index}`}
                                onClick={handleClickBrand.bind(this, brand.name)}>
                                <div className={styles['pref-content-brand-title']}>{brand.title}</div>
                                <div className={styles['pref-content-brand-content']}>{brand.desc}</div>
                                <Radio className={styles['pref-content-brand-radio']} value={brand.name}></Radio>
                            </div>
                        );
                    })}
                </div>
            </RadioGroup>
            <div className={styles['pref-content-style-container']}>
                <div className={styles['title']}>Style</div>
                <div className={styles['pref-content-style-styles']}>
                    {styleOptions.map((style, index) => {
                        return (
                            <div
                                className={classnames({
                                    [styles['pref-content-style-style']]: true,
                                    [styles['selected']]: selectedStyle === style.name,
                                })}
                                key={`style${index}`}
                                onClick={handleClickStyle.bind(this, style.name)}>
                                <div>{style.title}</div>
                                <style.img />
                            </div>
                        );
                    })}
                    <FileUploadIcon onFileUpload={handleUploadStyle}></FileUploadIcon>
                </div>
                {uploadedStyle && (
                    <div className={styles['pref-content-file-name']}>
                        <div className={styles['pref-content-file-details']}>
                            <span className="Appkit4-icon icon-document-file-outline"></span>
                            <span> {uploadedStyle?.name} </span>
                            <span className={styles['size']}> {`${Math.round(uploadedStyle.size / 1024)}kb`}</span>
                        </div>
                        <span
                            className={classnames([
                                'Appkit4-icon',
                                'icon-success-outline',
                                styles['pref-content-file-icon'],
                            ])}></span>
                    </div>
                )}
            </div>
            <div className={styles['pref-content-logo-container']}>
                <div className={styles['title']}>Logo</div>
                <RadioGroup value={selectedLogo}>
                    <div className={styles['pref-content-logo']}>
                        {logoOptions.map((logoOption) => {
                            return (
                                <div
                                    className={classnames({
                                        [styles['pref-content-logo-item']]: true,
                                        [styles['selected']]: selectedLogo === logoOption.name,
                                    })}
                                    key={`logoOption-${logoOption.name}`}
                                    onClick={handleClickLogo.bind(this, logoOption.name)}>
                                    <logoOption.img />
                                    <Radio
                                        className={styles['pref-content-logo-item-radio']}
                                        value={logoOption.name}></Radio>
                                </div>
                            );
                        })}
                        <FileUploadIcon onFileUpload={handleUploadLogo}></FileUploadIcon>
                    </div>
                </RadioGroup>
                {uploadedLogo && (
                    <div className={styles['pref-content-file-name']}>
                        <div className={styles['pref-content-file-details']}>
                            <span className="Appkit4-icon icon-document-file-outline"></span>
                            <span> {uploadedLogo?.name} </span>
                            <span className={styles['size']}> {`${Math.round(uploadedLogo.size / 1024)}kb`}</span>
                        </div>
                        <span
                            className={classnames([
                                'Appkit4-icon',
                                'icon-success-outline',
                                styles['pref-content-file-icon'],
                            ])}></span>
                    </div>
                )}
            </div>
            <div className={styles['pref-content-theming-container']}>
                <div className={styles['title']}>Theming</div>
                <div className={styles['pref-content-themes']}>
                    {themingOptions.map((theme) => {
                        const isSelected = theme.name === selectedTheme;
                        return (
                            <div
                                className={classnames({
                                    [styles['pref-content-theme']]: true,
                                    [styles['selected']]: isSelected,
                                })}
                                key={`theme-${theme.name}`}
                                style={{ backgroundColor: theme.name }}
                                onClick={handleSelectTheme.bind(this, theme.name)}>
                                {isSelected && <span className="Appkit4-icon icon-circle-checkmark-outline"></span>}
                                <span className={styles['pref-content-theme-name']}>{capitalize(theme.name)}</span>
                            </div>
                        );
                    })}
                </div>
            </div>
            <div className={styles['pref-content-button-styling-container']}>
                <div className={styles['title']}>Corners</div>
                <div className={styles['pref-content-button-stylings']}>
                    {buttonStylingOptions.map((buttonStyling) => {
                        return (
                            <div
                                className={classnames({
                                    [styles['pref-content-button-styling']]: true,
                                    [styles[`pref-content-button-styling-${buttonStyling.name}`]]: true,
                                    [styles['selected']]: selectedCorner === buttonStyling.name,
                                })}
                                key={`buttonStyling-${buttonStyling.name}`}
                                onClick={handleSelectCorner.bind(this, buttonStyling.name)}>
                                <span>{capitalize(buttonStyling.name)}</span>
                            </div>
                        );
                    })}
                </div>
            </div>
            <div className={styles['pref-content-notification']}>
                <span className="Appkit4-icon icon-warning-outline"></span>
                <span className={styles['text']}>
                    These styles are only applied to new changes and not existing designs
                </span>
            </div>
        </div>
    );
};

const Preferences = () => {
    const { showPreferences, setShowPreferences } = useContext(PreferencesContext);
    const [selectedBrand, setBrand] = useState<string | null>(null);
    const [selectedStyle, setStyle] = useState<string | null>(null);
    const [uploadedStyle, setUploadedStyle] = useState<File | null>(null);
    const [selectedLogo, setLogo] = useState<string | null>(null);
    const [uploadedLogo, setUploadedLogo] = useState<File | null>(null);
    const [selectedTheme, setSelectedTheme] = useState<string | null>(null);
    const [selectedCorner, setCorner] = useState<string | null>(null);

    const handleCancel = () => {
        setShowPreferences(false);
        clearPreferences();
    };
    const handleSave = () => {};
    const handleSaveAndRegenerate = () => {};

    const clearPreferences = () => {
        setBrand(null);
        setStyle(null);
        setUploadedStyle(null);
        setLogo(null);
        setUploadedLogo(null);
        setSelectedTheme(null);
        setCorner(null);
    };

    return (
        <Modal
            visible={showPreferences}
            title="Preferences"
            onCancel={() => {
                setShowPreferences(false);
                clearPreferences();
            }}
            modalStyle={{ width: '720px', maxHeight: '80vh' }}
            bodyStyle={{ maxHeight: '60vh', paddingBottom: '50px' }}
            maskCloseable={false}
            footer={
                <PreferencesFooter
                    onCancel={handleCancel}
                    onSave={handleSave}
                    onSaveAndRegenerate={handleSaveAndRegenerate}></PreferencesFooter>
            }
            footerStyle={{ padding: 'var(--spacing-6)' }}>
            <PreferencesContent
                selectedBrand={selectedBrand}
                setBrand={setBrand}
                selectedStyle={selectedStyle}
                setStyle={setStyle}
                uploadedStyle={uploadedStyle}
                setUploadedStyle={setUploadedStyle}
                selectedLogo={selectedLogo}
                setLogo={setLogo}
                uploadedLogo={uploadedLogo}
                setUploadedLogo={setUploadedLogo}
                selectedTheme={selectedTheme}
                setSelectedTheme={setSelectedTheme}
                selectedCorner={selectedCorner}
                setCorner={setCorner}></PreferencesContent>
        </Modal>
    );
};

export default Preferences;
